import { useState } from "react";
import {
  AlertTriangle,
  Download,
  ExternalLink,
  Pencil,
  RefreshCw,
  Wand2,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { AppBindingHealthCheckResponse } from "./hooks/useAppBindings";

const CONNECTIVITY_MSG: Record<string, string> = {
  unauthorized:
    "保存済み kintone API トークンが拒否されています。",
  forbidden:
    "保存済み kintone API トークンに必要な権限がありません。",
  not_found:
    "対象アプリに到達できません。アプリ URL・公開状態・削除されていないかを確認してください。",
  unavailable:
    "kintone に接続できませんでした。時間をおいて「再チェック」してください。",
  identity_mismatch:
    "保存済みのアプリ／スペース情報が実際の kintone と一致しません。設定を確認してください。",
};

const PLUGIN_MSG: Record<string, string> = {
  not_present:
    "プラグインがアプリに追加されていません。",
  permission_required:
    "プラグイン追加状態を確認する権限が不足しています。",
  unavailable: "プラグインの追加状態を確認できませんでした。",
};

export function HealthWarningBanner({
  result,
  error,
  isLoading,
  onRecheck,
  onRerunEasySetup,
  onEditManually,
  pluginPackageHref,
  systemPluginUrl,
  appPluginSettingsUrl,
  pluginPresenceDrift = false,
  suppressPluginNotPresent = false,
  suppressUpdateAvailable = false,
  reflectionPending = false,
  offerEasySetupForVersionRecovery = false,
  easySetupHasVerifiedCredential = false,
}: {
  result: AppBindingHealthCheckResponse | null;
  error: { code: string; message: string } | null;
  isLoading: boolean;
  onRecheck: () => void;
  onRerunEasySetup?: () => void;
  onEditManually?: () => void;
  pluginPackageHref?: string;
  systemPluginUrl?: string | null;
  appPluginSettingsUrl?: string | null;
  pluginPresenceDrift?: boolean;
  suppressPluginNotPresent?: boolean;
  suppressUpdateAvailable?: boolean;
  reflectionPending?: boolean;
  offerEasySetupForVersionRecovery?: boolean;
  easySetupHasVerifiedCredential?: boolean;
}) {
  const [manualOpen, setManualOpen] = useState(false);
  const items: string[] = [];
  let showRerun = false;
  let showManualEdit = false;
  let showPluginPackage = false;
  let showSystemPlugin = false;
  let showAppPluginSettings = false;
  let rerunLabel = "簡単セットアップを再実行";

  if (error) {
    // e.g. kintone_api_token_missing — surface the message + offer remediation.
    items.push(error.message);
    showRerun = true;
    showManualEdit = true;
  } else if (result) {
    if (result.connectivity.state === "failed") {
      const kind = result.connectivity.kind ?? "";
      items.push(CONNECTIVITY_MSG[kind] ?? "API トークンの疎通に失敗しました。");
      if (kind === "unauthorized" || kind === "forbidden") {
        showRerun = true;
        showManualEdit = true;
      }
      if (kind === "not_found" || kind === "identity_mismatch") {
        showManualEdit = true;
      }
    }
    const p = result.plugin.state;
    if (
      (p === "not_present" || p === "permission_required" || p === "unavailable") &&
      !(p === "not_present" && suppressPluginNotPresent)
    ) {
      items.push(
        p === "not_present" && pluginPresenceDrift
          ? "再利用する場合はプラグインの再追加と再連携が必要です。利用を終了する場合は、画面下部の「kintoneアプリの設定を削除」から設定を削除してください。"
          : PLUGIN_MSG[p] ?? "プラグインの状態に問題があります。",
      );
      if (p === "not_present") {
        showRerun = true;
        showPluginPackage = true;
        showSystemPlugin = true;
        showAppPluginSettings = pluginPresenceDrift;
      }
      if (p === "permission_required") {
        showRerun = true;
        showManualEdit = true;
      }
    }
    if (!pluginPresenceDrift && result.version.state === "incompatible") {
      items.push(
        `プラグインのバージョン対応が必要です。要求範囲: ${result.version.requiredRange}、最新: ${result.version.latest ?? "未設定"}。`,
      );
      if (offerEasySetupForVersionRecovery) {
        showRerun = true;
        rerunLabel = "簡単セットアップで更新";
      }
      showPluginPackage = true;
      showSystemPlugin = true;
    }
    if (
      !pluginPresenceDrift &&
      result.version.state === "update_available" &&
      !suppressUpdateAvailable
    ) {
      items.push(
        `プラグインの最新版（${result.version.latest ?? "未設定"}）があります。`,
      );
      if (offerEasySetupForVersionRecovery) {
        showRerun = true;
        rerunLabel = "簡単セットアップで更新";
      }
      showPluginPackage = true;
      showSystemPlugin = true;
    }
  }

  if (reflectionPending) {
    items.push("保存した設定が kintone 側へ未反映です。反映が完了するまで、保存済みの変更は適用されません。");
    showAppPluginSettings = true;
    rerunLabel = "簡単セットアップで更新・反映";
  }

  // Token connectivity and plugin presence do not prove the current contract linkage.
  const softNote = !error && result && result.plugin.state === "present"
    ? `現在のプラグイン設定とこの契約の連携は未確認です。${result.version.state === "unknown" ? "プラグインのバージョンも未確認です。" : ""}`
    : null;

  if (items.length === 0 && !softNote) return null;
  const isVersionRecovery =
    result?.version.state === "update_available" ||
    result?.version.state === "incompatible";
  const makeManualVersionActionsSubtle =
    easySetupHasVerifiedCredential &&
    showRerun &&
    isVersionRecovery;
  const hasManualActions =
    showManualEdit ||
    Boolean(showPluginPackage && pluginPackageHref) ||
    Boolean(showSystemPlugin && systemPluginUrl) ||
    Boolean(showAppPluginSettings && appPluginSettingsUrl);

  const recheckButton = (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={onRecheck}
      disabled={isLoading}
    >
      <RefreshCw className="mr-1 h-3.5 w-3.5" />
      {isLoading ? "確認中…" : "再チェック"}
    </Button>
  );

  if (items.length === 0 && softNote) {
    return (
      <div className="mb-4 flex items-center justify-between gap-3 rounded-md border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
        <span>{softNote}</span>
        {recheckButton}
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        <div className="flex-1 space-y-3">
          <div className="font-medium text-amber-900">
            {pluginPresenceDrift
              ? "kintoneアプリからプラグインが削除されています"
              : "kintone 側の状態に対応が必要です"}
          </div>
          <div className="space-y-1 text-sm text-amber-900">
            {items.map((message, index) => (
              <p key={index}>{message}</p>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {showRerun && onRerunEasySetup ? (
              <Button
                type="button"
                size="sm"
                onClick={onRerunEasySetup}
              >
                <Wand2 className="mr-1 h-3.5 w-3.5" />
                {rerunLabel}
              </Button>
            ) : null}
            {hasManualActions ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-expanded={manualOpen}
                onClick={() => setManualOpen((current) => !current)}
              >
                <Wrench className="mr-1 h-3.5 w-3.5" />
                手動セットアップ
              </Button>
            ) : null}
            {recheckButton}
          </div>
          {manualOpen && hasManualActions ? (
            <div className="rounded-md border border-amber-200 bg-background/80 p-3">
              <div className="mb-2 text-sm font-medium text-amber-950">
                手動セットアップに必要な操作
              </div>
              {reflectionPending ? (
                <p className="mb-3 text-sm text-amber-900">
                  プラグインを更新後、対象アプリのプラグイン設定画面で保存済みの設定を再反映してください。連携トークンの再発行は不要です。
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {showPluginPackage && pluginPackageHref ? (
                  <Button
                    type="button"
                    size="sm"
                    variant={makeManualVersionActionsSubtle ? "link" : "outline"}
                    asChild
                  >
                    <a href={pluginPackageHref}>
                      <Download className="mr-1 h-3.5 w-3.5" />
                      プラグイン(zip)をダウンロード
                    </a>
                  </Button>
                ) : null}
                {showSystemPlugin && systemPluginUrl ? (
                  <Button
                    type="button"
                    size="sm"
                    variant={makeManualVersionActionsSubtle ? "link" : "outline"}
                    asChild
                  >
                    <a href={systemPluginUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-1 h-3.5 w-3.5" />
                      プラグインの登録画面を開く
                    </a>
                  </Button>
                ) : null}
                {showAppPluginSettings && appPluginSettingsUrl ? (
                  <Button type="button" size="sm" variant="outline" asChild>
                    <a href={appPluginSettingsUrl} target="_blank" rel="noreferrer">
                      <ExternalLink className="mr-1 h-3.5 w-3.5" />
                      対象アプリのプラグイン設定画面を開く
                    </a>
                  </Button>
                ) : null}
                {showManualEdit && onEditManually ? (
                  <Button type="button" size="sm" variant="outline" onClick={onEditManually}>
                    <Pencil className="mr-1 h-3.5 w-3.5" />
                    設定を手動で編集
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
